import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  usePatchApiCompaniesCurrentServicemodulesIdIcon,
} from '@/api/generated/company-management-user/company-management-user'
import { notify } from '@/lib/notify'
import type { ServiceModuleModel, UpdateServiceModuleModel } from '@/api/model'
import { LocalizationEntityType } from '@/api/model/localizationEntityType'
import { useEntityTranslations } from '@/components/common/translations/useEntityTranslations'

const TRANSLATABLE_PROPERTIES = ['name']

/**
 * State + payload building of a module's standard fields (column 1 of the editor).
 * Every type-specific payload is a superset of this — the forms call
 * `buildBasePayload()` and add their own fields.
 *
 * Symbol = image OR icon+color (exclusive, like action modules). The image
 * upload/delete goes through a dedicated endpoint and takes effect immediately
 * (not only on save); iconName/colorHex are kept.
 */
export interface StandardModuleFieldsController {
  name: string
  setName: (v: string) => void
  iconName: string | null
  setIconName: (v: string | null) => void
  iconUrl: string | null
  iconBusy: boolean
  uploadIcon: (file: File, dataUrl: string) => void
  deleteIcon: () => void
  colorHex: string
  setColorHex: (v: string) => void
  isVisible: boolean
  setIsVisible: (v: boolean) => void
  sendNotification: boolean
  setSendNotification: (v: boolean) => void
  isCompanyWide: boolean
  setIsCompanyWide: (v: boolean) => void
  selectedGroupIds: number[]
  setSelectedGroupIds: (v: number[]) => void
  nameTranslations: Record<string, string>
  setNameTranslations: (v: Record<string, string>) => void
  /** Call after the module PATCH — persists changed name translations. */
  saveTranslations: () => Promise<void>
  buildBasePayload: () => UpdateServiceModuleModel
}

export function useStandardServiceModuleFields(module: ServiceModuleModel): StandardModuleFieldsController {
  const { t } = useTranslation('service-modules')
  const [name, setName] = useState(module.name ?? '')
  const [iconName, setIconName] = useState<string | null>(module.iconName ?? null)
  const [iconUrl, setIconUrl] = useState<string | null>(module.iconUrl ?? null)
  const [colorHex, setColorHex] = useState(module.colorHex ?? "#43B8BB")
  const [isVisible, setIsVisible] = useState(module.isVisible ?? true)
  const [sendNotification, setSendNotification] = useState(module.sendNotification ?? false)
  const [isCompanyWide, setIsCompanyWide] = useState(module.isCompanyWideAssigned ?? true)
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>(module.assignedGroupIds ?? [])

  const queryClient = useQueryClient()
  const patchIcon = usePatchApiCompaniesCurrentServicemodulesIdIcon()
  const translations = useEntityTranslations({
    entityType: LocalizationEntityType.ServiceModule,
    entityId: String(module.id),
    properties: TRANSLATABLE_PROPERTIES,
  })

  // Image upload goes through a dedicated endpoint and takes effect immediately
  // (not only on save); iconName/colorHex are kept. Backend expects the file as
  // multipart FormData (`File`); sending `File: null` clears/removes the icon.
  async function uploadIcon(file: File, dataUrl: string) {
    try {
      await patchIcon.mutateAsync({ id: module.id, data: { File: file } })
      setIconUrl(dataUrl)
      await queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey() })
    } catch (error) {
      notify.apiError(error, t('toasts.imageUploadError'))
    }
  }

  async function deleteIcon() {
    try {
      await patchIcon.mutateAsync({ id: module.id, data: { File: undefined } })
      setIconUrl(null)
      await queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey() })
    } catch (error) {
      notify.apiError(error, t('toasts.imageRemoveError'))
    }
  }

  const buildBasePayload = (): UpdateServiceModuleModel => ({
    name: name || null,
    iconName: iconName || null,
    colorHex: colorHex || null,
    sendNotification,
    isVisible,
    isCompanyWideAssigned: isCompanyWide,
    // company-wide → no groups; otherwise the selected ones
    assignedGroupIds: isCompanyWide ? [] : selectedGroupIds,
  })

  return {
    name, setName,
    iconName, setIconName,
    iconUrl, iconBusy: patchIcon.isPending, uploadIcon, deleteIcon,
    colorHex, setColorHex,
    isVisible, setIsVisible,
    sendNotification, setSendNotification,
    isCompanyWide, setIsCompanyWide,
    selectedGroupIds, setSelectedGroupIds,
    nameTranslations: translations.get('name'),
    setNameTranslations: (v) => translations.set('name', v),
    saveTranslations: () => translations.save(String(module.id)),
    buildBasePayload,
  }
}
